import React, { useState } from 'react';
import { TAP_TARGET_PHONE } from '../constants/buttons';
import { toLocalISO } from '../utils/dateFormat';

/**
 * Compact time range selector for individual chart sections.
 * Provides quick preset buttons: All, Week, Month, Quarter, Year
 * Each chart can independently control its own date range.
 */

const LS_PREFIX = 'sfx_chart_range_';

function getPresetDates(preset) {
  const now = new Date();
  const today = toLocalISO(now);

  switch (preset) {
    case 'week': {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      return { start_date: toLocalISO(monday), end_date: today };
    }
    case 'month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start_date: toLocalISO(start), end_date: today };
    }
    case 'quarter': {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      const start = new Date(now.getFullYear(), qMonth, 1);
      return { start_date: toLocalISO(start), end_date: today };
    }
    case 'year': {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start_date: toLocalISO(start), end_date: today };
    }
    case 'all':
    default:
      return { start_date: '', end_date: '' };
  }
}

const PRESETS = [
  { key: 'all', label: 'All' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
];

function ChartTimeRange({ chartId, onChange, className = '' }) {
  const lsKey = LS_PREFIX + chartId;

  const [active, setActive] = useState(() => {
    try {
      return localStorage.getItem(lsKey) || 'all';
    } catch {
      return 'all';
    }
  });

  const handlePreset = (preset) => {
    setActive(preset);
    try {
      localStorage.setItem(lsKey, preset);
    } catch {}
    const dates = getPresetDates(preset);
    onChange(dates);
  };

  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid={`chart-time-range-${chartId}`}>
      {PRESETS.map(p => (
        <button
          key={p.key}
          onClick={() => handlePreset(p.key)}
          // 📱 Below md: ≥44px wide and a 44px-tall invisible hit box (mobile batch item 19).
          className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors max-md:min-w-[44px] ${TAP_TARGET_PHONE} ${
            active === p.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
          }`}
          data-testid={`chart-range-${chartId}-${p.key}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// Helper to get initial date range from localStorage for a chart
export function getChartInitialRange(chartId) {
  try {
    const preset = localStorage.getItem(LS_PREFIX + chartId) || 'all';
    return getPresetDates(preset);
  } catch {
    return { start_date: '', end_date: '' };
  }
}

export default ChartTimeRange;
